import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

// 4 hours of true inactivity (no mouse/keys/touch/scroll) before auto-logout.
// Previously 10 min which was way too aggressive for an admin tool — admins
// would read a lead, step away briefly, and come back logged out.
const INACTIVITY_MS = 4 * 60 * 60 * 1000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);
  const userRef = useRef(null);

  // Keep a ref of `user` so resetTimer doesn't need to depend on it.
  useEffect(() => { userRef.current = user; }, [user]);

  const logout = useCallback(() => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    setUser(null);
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const resetTimer = useCallback(() => {
    if (!userRef.current) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      logout();
      window.dispatchEvent(new CustomEvent("session-expired"));
    }, INACTIVITY_MS);
  }, [logout]);

  useEffect(() => {
    const events = ["mousedown", "mousemove", "keydown", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, resetTimer));
    return () => events.forEach((e) => window.removeEventListener(e, resetTimer));
  }, [resetTimer]);

  // Bootstrap: run ONCE on mount. The previous version depended on
  // `resetTimer` which changed every time `user` updated → infinite
  // /auth/me polling loop. Any transient failure in that loop would
  // silently wipe localStorage tokens, leaving the UI looking logged in
  // but the next mutation returning 401 → kicked to /login.
  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    api.get("/auth/me")
      .then(({ data }) => {
        if (cancelled) return;
        setUser(data.user);
        resetTimer();
      })
      .catch((err) => {
        // Only clear tokens if the server confirmed the credentials are
        // bad (the response interceptor already handled refresh on 401).
        // Network errors / 5xx / aborted requests must NOT silently log
        // the user out.
        if (err?.response?.status === 401) {
          localStorage.removeItem("access_token");
          localStorage.removeItem("refresh_token");
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (username, password) => {
    const { data } = await api.post("/auth/login", { username, password });
    localStorage.setItem("access_token", data.access_token);
    localStorage.setItem("refresh_token", data.refresh_token);
    setUser(data.user);
    resetTimer();
    return data.user;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
